import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { DEFAULT_FUNCTIONS } from 'src/app/core/constants/default-functions';
import { Connection } from 'src/app/core/models/connection.model';
import { Line } from 'src/app/core/models/line.model';
import { MovementTransferType } from 'src/app/core/models/load/analysis/movement-transfer-type';
import { BoundaryCondition } from 'src/app/core/models/load/node/boundary-condition.enum';
import { NodeType } from 'src/app/core/models/load/node/node-type.enum';
import { Node } from 'src/app/core/models/load/node/node.model';
import { PrescribedMovementAssembly } from 'src/app/core/models/load/node/prescribed-movement-assembly.model';
import { ReferenceType } from 'src/app/core/models/reference-type.enum';
import { Simulation } from 'src/app/core/models/simulation.model';
import { ObjectState } from 'src/app/shared/utils/object-state';
import { DeleteRequestAnswer } from 'src/app/shared/utils/simulation-manager-aux/delete-request-answer';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { MapsService } from '../../objects/maps/maps.service';
import { ReferenceSystemService } from '../../objects/reference-system/reference-system.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { AnalysisService } from '../analysis/analysis.service';
import { LoadService } from '../load.service';

@Injectable({
  providedIn: 'root',
})
export class NodesService {
  filterParams = {
    nodeType$: new BehaviorSubject<NodeType>(NodeType.CONNECTION),
    line$: new BehaviorSubject<number>(-1),
  };

  constructor(
    protected simulationService: SimulationManagerService,
    protected dataSharingService: DataSharingService,
    protected mapsService: MapsService,
    protected loadService: LoadService,
    protected analysisService: AnalysisService,
    protected referenceSystemService: ReferenceSystemService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  get currentType(): NodeType {
    return this.filterParams.nodeType$.getValue();
  }

  get currentLineFilter(): number {
    return this.filterParams.line$.getValue();
  }

  updateNodeType(nodeType: NodeType): void {
    this.filterParams.nodeType$.next(nodeType);
  }

  updateLineFilter(lineId: number): void {
    this.filterParams.line$.next(lineId);
  }

  getNodesByCurrentFilterParams(): Node[] {
    return this.simulation.node.filter(({ type, referenceId }) => {
      if (
        type === this.currentType &&
        this.currentType === NodeType.SEGMENT_NODE &&
        (this.currentLineFilter === -1 || this.currentLineFilter === referenceId)
      ) {
        return true;
      }

      if (type === this.currentType) {
        return true;
      }

      return false;
    });
  }

  getNodesByIdAndType(id: number, selectedType: NodeType): Node[] {
    return this.simulation.node.filter(({ type, referenceId }) => {
      const allOrConnection =
        selectedType === null ||
        (selectedType === NodeType.CONNECTION && type === NodeType.CONNECTION);

      if (id === -1 && allOrConnection) {
        return true;
      }

      if (id === referenceId && (selectedType === type || type === NodeType.SEGMENT_NODE)) {
        return true;
      }

      return false;
    });
  }

  getDefaultPrescMov(): PrescribedMovementAssembly[] {
    const { title: functionName } = DEFAULT_FUNCTIONS.TIME[0];
    const amplitude = 0;
    return new Array(6).fill({ amplitude, functionName });
  }

  getDefaultBoundaryContiditions(type: NodeType): BoundaryCondition[] {
    const boundaryConditions = {
      CONNECTION: new Array(6).fill(BoundaryCondition.PRESCRIBED),
      ANCHOR: new Array(6).fill(BoundaryCondition.RESTRAINED),
      SEGMENT_NODE: new Array(6).fill(BoundaryCondition.FREE),
    };
    return type ? boundaryConditions[type] : boundaryConditions.CONNECTION;
  }

  getNodeTitle(origin: Connection | Line, type: NodeType): string {
    const titleAffix = {
      ANCHOR: CONSTANTS.ANCHOR_AFFIX,
      SEGMENT_NODE: CONSTANTS.SEG_AFFIX,
      CONNECTION: '',
    };
    return `${origin.title}${titleAffix[type]}`;
  }

  create(origin: Connection | Line, type: NodeType): Node {
    const newNode: Node = new Node();
    const id = this.simulationService.getNewId();

    newNode.internalId = id;
    newNode.title = this.getNodeTitle(origin, type);
    newNode.referenceId = origin.internalId;
    newNode.type = type;
    newNode.boundaryCondition = this.getDefaultBoundaryContiditions(type);
    newNode.spring = [0, 0, 0, 0, 0, 0];
    newNode.assemblyReferenceSystem = ReferenceType.GLOBAL;
    newNode.anForcesReferenceSystem = ReferenceType.GLOBAL;
    newNode.prescMovementAssembly = this.getDefaultPrescMov();
    newNode.prescMovAssemblyNodalForces = this.getDefaultPrescMov();
    newNode.loads = [];

    this.simulation.node.push(newNode);

    this.analysisService.createMovementsForNewReferenceObject(
      newNode.internalId,
      MovementTransferType.NODE,
    );

    return newNode;
  }

  refreshReferences(origin: Connection | Line, type: NodeType): void {
    const nodeToUpdate = this.simulation.node.find(
      (node) => node.referenceId === origin.internalId,
    );
    if (nodeToUpdate) {
      nodeToUpdate.title = this.getNodeTitle(origin, type);
    }
  }

  refreshSegmentNodes(line: Line): void {
    const alreadyConsideredSegIndexes = [];
    this.simulation.node = this.simulation.node.filter((node) => {
      const matchingOrigin = node.referenceId === line.internalId;

      if (!matchingOrigin) {
        return true;
      }

      const segmentType = node.type === NodeType.SEGMENT_NODE;
      if (!segmentType) {
        return true;
      }

      const splittedNodeTitle = node.title.split(`${CONSTANTS.SEG_AFFIX}_`);
      const nodeSegIndex = +splittedNodeTitle[splittedNodeTitle.length - 1] - 1;
      const hasMoreThanOneSeg = line.segConfig.length > 1;
      const relatedSegStillExists = nodeSegIndex < line.segConfig.length - 1;
      if (hasMoreThanOneSeg && relatedSegStillExists) {
        alreadyConsideredSegIndexes.push(nodeSegIndex);
        return true;
      }

      this.loadService.deleteMovementsByReferenceId(node.internalId);
      return false;
    });
    this.createNewSegmentNodes(line, alreadyConsideredSegIndexes);
  }

  createNewSegmentNodes(line: Line, alreadyConsideredSegIndexes: number[]): void {
    line.segConfig.forEach((_, index) => {
      const isNotLast = index < line.segConfig.length - 1;
      if (isNotLast && !alreadyConsideredSegIndexes.includes(index)) {
        const newNode = this.create(line, NodeType.SEGMENT_NODE);
        newNode.title += `_${index + 1}`;
      }
    });
  }

  saveRestraints(boundaryCondition: BoundaryCondition[][]): void {
    const nodesInCurrentEdition = this.getNodesByCurrentFilterParams();
    nodesInCurrentEdition.forEach((node, index) => {
      node.boundaryCondition = boundaryCondition[index];
    });

    this.simulationService.changeSimulation(false);
    this.dataSharingService.localChanges$.next();
  }

  saveSpring(spring: number[][]): void {
    const nodesInCurrentEdition = this.getNodesByCurrentFilterParams();
    nodesInCurrentEdition.forEach((node, index) => {
      node.spring = spring[index];
    });

    this.simulationService.changeSimulation(false);
  }

  deleteByReferenceId(id: number): DeleteRequestAnswer {
    const deletedIds = new Set<number>();
    deletedIds.add(id);

    this.simulation.node = this.simulation.node.filter(({ referenceId, internalId }) => {
      const idMatches = referenceId === id;
      if (idMatches) {
        deletedIds.add(internalId);
      }
      return !idMatches;
    });

    this.referenceSystemService.resetByConnectionNodeId(deletedIds);
    this.analysisService.deleteNodeReferencesById(deletedIds);

    return new DeleteRequestAnswer(ObjectState.objectWasDeleted, null);
  }
}
